// src/services/authService.js
import api from './api';

/**
 * Sign in user with username and password
 * @param {string} username - Admin username
 * @param {string} password - Admin password
 * @returns {Promise<Object>} Login response with token and user data
 */
export const signInUser = async (username, password) => {
  try {
    // Create form data as required by FastAPI OAuth2PasswordRequestForm
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await api.post('/api/admin/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    // Backend returns { success: true, data: {...}, message: "..." } format
    const responseData = response.data.data || response.data;
    const { access_token, refresh_token, user } = responseData;

    // Store tokens and user data in localStorage
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user_data', JSON.stringify(user));

    return {
      token: access_token,
      refreshToken: refresh_token,
      user: user,
      message: response.data.message || 'Login successful',
    };
  } catch (error) {
    console.error('Login error:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Login failed. Please check your credentials.';
    throw new Error(errorMessage);
  }
};

/**
 * Sign out user and clear stored data
 * @returns {Promise<Object>} Logout response
 */
export const signOutUser = async () => {
  try {
    // Clear all stored authentication data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');

    return { message: 'Logged out successfully' };
  } catch (error) {
    console.error('Logout error:', error);
    // Even if there's an error, clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    
    return { message: 'Logged out successfully' };
  }
};

/**
 * Get current logged-in user profile
 * @returns {Promise<Object>} User profile data
 */
export const getLoggedInUserProfile = async () => {
  try {
    const response = await api.get('/api/admin/profile/');
    
    // Backend returns { success: true, data: { profile: {...}, login_history: [...], activity_stats: {...} }, message: "..." }
    const responseData = response.data.data || response.data;
    
    // Update stored user data with fresh profile data from server
    if (responseData.profile) {
      localStorage.setItem('user_data', JSON.stringify(responseData.profile));
    }
    
    return responseData;
  } catch (error) {
    console.error('Get profile error:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to get user profile';
    throw new Error(errorMessage);
  }
};

/**
 * Change user password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Change password response
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.put('/api/admin/profile/password', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: newPassword
    });

    return response.data;
  } catch (error) {
    console.error('Change password error:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to change password';
    throw new Error(errorMessage);
  }
};

/**
 * Update user profile
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} Update profile response
 */
export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/api/admin/profile/', profileData);
    
    // Backend returns { success: true, data: {...}, message: "..." } format
    const responseData = response.data.data || response.data;
    
    // Update stored user data with fresh profile data
    if (responseData) {
      localStorage.setItem('user_data', JSON.stringify(responseData));
    }
    
    return response.data;
  } catch (error) {
    console.error('Update profile error:', error);
    console.error('Error response:', error.response?.data);
    
    // Handle validation errors (422)
    if (error.response?.status === 422) {
      const validationErrors = error.response.data?.detail;
      if (Array.isArray(validationErrors)) {
        const errorMessages = validationErrors.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        throw new Error(`Validation error: ${errorMessages}`);
      } else if (typeof validationErrors === 'string') {
        throw new Error(`Validation error: ${validationErrors}`);
      }
    }
    
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to update profile';
    throw new Error(errorMessage);
  }
};

/**
 * Upload user avatar
 * @param {File} avatarFile - Avatar image file
 * @returns {Promise<Object>} Upload avatar response
 */
export const uploadUserAvatar = async (avatarFile) => {
  try {
    const formData = new FormData();
    formData.append('avatar', avatarFile);

    const response = await api.put('/api/admin/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Update stored user data if avatar URL is returned
    const responseData = response.data.data || response.data;
    if (responseData && responseData.avatar_url) {
      const userData = getStoredUserData();
      if (userData) {
        userData.avatar_url = responseData.avatar_url;
        localStorage.setItem('user_data', JSON.stringify(userData));
      }
    }

    return response.data;
  } catch (error) {
    console.error('Upload avatar error:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to upload avatar';
    throw new Error(errorMessage);
  }
};

/**
 * Remove user avatar
 * @returns {Promise<Object>} Remove avatar response
 */
export const removeUserAvatar = async () => {
  try {
    const response = await api.delete('/api/admin/profile/avatar');

    // Update stored user data to remove avatar URL
    const userData = getStoredUserData();
    if (userData) {
      userData.avatar_url = null;
      localStorage.setItem('user_data', JSON.stringify(userData));
    }

    return response.data;
  } catch (error) {
    console.error('Remove avatar error:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to remove avatar';
    throw new Error(errorMessage);
  }
};

/**
 * Request password reset via email
 * @param {string} email - User email address
 * @returns {Promise<Object>} Forgot password response
 */
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/api/admin/auth/forgot-password', {
      email: email,
    });

    return response.data;
  } catch (error) {
    console.error('Forgot password error:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to send password reset email';
    throw new Error(errorMessage);
  }
};

/**
 * Verify reset code and set new password
 * @param {string} email - User email address
 * @param {string} code - Verification code
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Reset password response
 */
export const verifyResetCode = async (email, code, newPassword) => {
  try {
    const response = await api.post('/api/admin/auth/verify-code', {
      email: email,
      code: code,
      new_password: newPassword,
    });

    return response.data;
  } catch (error) {
    console.error('Verify reset code error:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to reset password';
    throw new Error(errorMessage);
  }
};

/**
 * Check if user is authenticated with token validation
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  const userData = localStorage.getItem('user_data');
  return !!(token && userData);
};

/**
 * Enhanced authentication check with token expiry validation
 * @returns {boolean} Authentication status
 */
export const checkAuthStatus = () => {
  const token = localStorage.getItem('access_token');
  const userData = localStorage.getItem('user_data');
  
  if (!token || !userData) {
    return false;
  }
  
  try {
    // Parse JWT token to check expiry (basic client-side check)
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token is expired (with 5 minute buffer for token refresh)
    if (tokenPayload.exp && tokenPayload.exp < (currentTime + 300)) {
      console.log('🔐 Token is expired or expiring soon');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error parsing token:', error);
    return false;
  }
};

/**
 * Get stored user data
 * @returns {Object|null} User data or null if not found
 */
export const getStoredUserData = () => {
  try {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    return null;
  }
};